import { useEffect, useState } from 'react';
import {
  AlloyButton,
  ChildModal,
  StickySection,
  useModal,
  ComponentLoader
} from 'cat-ecommerce-alloy';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import { useBreakpoint } from '@app/hooks';
import AlternativePartsModal from '../AlternativePartsModal';
import ReplacementPartsTable from './ReplacementPartsTable/ReplacementPartsTable';
import QOStyles from '../styles.module.scss';
import {
  ADD_TO_CART_MODIFICATIONS_RESET,
  ADD_TO_CART_SET_PHASE,
  GET_DEALER_PRICE_AND_AVAILABILITY_RESET,
  RESET_CURRENT_MODAL_PHASE,
  SET_CURRENT_MODAL_PHASE,
  UPDATE_PARTS
} from '@app/store/checkout/constants';
import { RESET_REPLACED_PARTS_INFO } from '@app/store/orders/constants';
import { STATUS, PAGE_BREAKPOINTS } from '@app/constants/commonConstants';
import { ERROR_DOMAIN, ERROR_PATH } from '@app/constants/errorConstants';
import { clearError, setError } from '@app/store/exception/actions';
import { getReplacementItemsToSend } from './utils';
import { setInvalidQuantityErrors } from '../utils';
import {
  ADD_TO_CART_PHASES,
  REPLACEMENT
} from '../../ShoppingCartPage/constants';
import ExceptionFeedbackModal from '../common/ExceptionFeedbackModal/ExceptionFeedbackModal';
import PreferenceToggle from '../common/PreferenceToggle';
import Disclaimers from '@app/components/pages/checkout/shopping-cart/QuickOrder/common/Disclaimers';
import { formatPartNumber, getAltParts } from '@app/store/checkout/utils';
import { useValidateQuantity } from '../hooks';

const ReplacementPartsModal = ({
  modalOptions,
  isChildModal,
  isAvailabilityToggled,
  setIsAvailabilityToggled
}) => {
  const [t] = useTranslation();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const dispatch = useDispatch();

  const replacementHasReplacement = useSelector(
    s => !!s.orders.replacedParts?.length
  );
  const { values: quickOrderValues, setErrors: setQOErrors } =
    useFormikContext();

  const items =
    useSelector(s => s.checkout.currentAddToCartModifications.items) ?? [];

  const dealerPriceAndAvailabilityStatus = useSelector(
    s => s.checkout.dealerPriceAndAvailabilityStatus
  );
  const addToCartStatus = useSelector(s => s.checkout.addToCartStatus);
  const isOrderPriceAvailabilityLoading = useSelector(
    s => s.orders.isOrderPriceAvailabilityLoading
  );
  const { validateItemQuantity } = useValidateQuantity();

  const showBack = !isMD || isChildModal;
  const isDealerPriceAndAvailabilityLoading =
    dealerPriceAndAvailabilityStatus === STATUS.PENDING;
  const isAddToCartStatusLoading = addToCartStatus === STATUS.PENDING;
  const isFetchingInfo =
    isOrderPriceAvailabilityLoading ||
    isAddToCartStatusLoading ||
    isDealerPriceAndAvailabilityLoading;

  const childModalOptions = useModal({
    isChild: true,
    parentIsOpen: modalOptions.isOpen
  });

  const alternateModalTitle = t('ALTERNATIVE_PARTS');
  const backButtonMessage = t('BACK_TO_REPLACEMENT_PARTS_MODAL');

  const [currentItems] = useState(items);

  /**
   * Resets currentAddToCartModifications to original values if user has
   * returned to replacement modal from child modals (i.e. Alternate parts modal)
   */
  useEffect(() => {
    if (!childModalOptions.isOpen) {
      dispatch({ type: UPDATE_PARTS, payload: currentItems });
    }
  }, [childModalOptions.isOpen, dispatch, currentItems]);

  useEffect(() => {
    dispatch({ type: SET_CURRENT_MODAL_PHASE, payload: REPLACEMENT });
  }, []);

  const handleChildModalClose = () => {
    dispatch(clearError(ERROR_DOMAIN.QUICK_ORDER, ERROR_PATH.ADD_TO_CART));
    childModalOptions.close();
  };

  const onBackCancel = cancelOption => {
    if (replacementHasReplacement || !isChildModal || cancelOption) {
      dispatch({ type: GET_DEALER_PRICE_AND_AVAILABILITY_RESET });
      dispatch({ type: ADD_TO_CART_MODIFICATIONS_RESET });
      dispatch({ type: RESET_REPLACED_PARTS_INFO });
    }
    dispatch(clearError(ERROR_DOMAIN.QUICK_ORDER, ERROR_PATH.ADD_TO_CART));
    dispatch({ type: RESET_CURRENT_MODAL_PHASE });
    modalOptions.close();
  };

  const onUpdate = () => {
    const itemsToSend = getReplacementItemsToSend(currentItems);
    const itemsToSendWithAlternatives = Object.keys(itemsToSend).reduce(
      (acc, curr) =>
        acc.concat(
          getAltParts(
            itemsToSend[curr],
            itemsToSend[curr].originalReplacedPartId
          )
        ),
      []
    );
    const quantityErrors = Object.values(itemsToSend).reduce((acc, curr) => {
      const isItemQuantityError = validateItemQuantity(
        curr,
        curr.requestedQuantity
      );
      const quantityObject = {
        originalReplacedPartId: curr.originalReplacedPartId,
        partNumber: formatPartNumber(curr.partNumber)
      };
      isItemQuantityError &&
        acc.indexOf(quantityObject) === -1 &&
        acc.push(quantityObject);
      return acc;
    }, []);
    const allQuantityErrorsHaveAlternateParts = quantityErrors.every(curr =>
      itemsToSendWithAlternatives.some(
        item =>
          item.originalPartReferenceId === curr.originalReplacedPartId &&
          item.originalPartNumber === curr.partNumber
      )
    );
    if (quantityErrors?.length && !allQuantityErrorsHaveAlternateParts) {
      dispatch({ type: GET_DEALER_PRICE_AND_AVAILABILITY_RESET });
      dispatch({ type: ADD_TO_CART_MODIFICATIONS_RESET });
      setInvalidQuantityErrors(
        quantityErrors.map(item => item.originalReplacedPartId),
        quickOrderValues,
        setQOErrors,
        t
      );
      dispatch(
        setError(ERROR_DOMAIN.QUICK_ORDER, ERROR_PATH.MAIN, {
          title: t('UNKNOWN_ERROR_TITLE'),
          message: t('QO_MAXIMUM_QUANTITY_UPDATED_ERROR')
        })
      );
      modalOptions.close();
    } else {
      dispatch({ type: UPDATE_PARTS, payload: itemsToSend });
      dispatch({ type: RESET_CURRENT_MODAL_PHASE });
      if (itemsToSendWithAlternatives.length) {
        childModalOptions.open();
      } else {
        dispatch({
          type: ADD_TO_CART_SET_PHASE,
          payload: ADD_TO_CART_PHASES.ADD_PARTS_TO_CART
        });
      }
    }
  };

  const errorSelector = state =>
    state.errors[ERROR_DOMAIN.QUICK_ORDER]?.[ERROR_PATH.ADD_TO_CART];
  const error = useSelector(errorSelector);

  return (
    <>
      {isFetchingInfo && <ComponentLoader contentClassName="position-sticky" />}
      <ExceptionFeedbackModal
        errorSelector={errorSelector}
        tryAgainButtonProps={{ onClick: onUpdate }}
        onCancelImportButton={() => onBackCancel(showBack)}
      />
      <div className="u-fs--16 u-color--DarkGray mb-3 mb-lg-2">
        {t('REPLACEMENT_PARTS_MODAL_SUBTITLE')} {t('ORIGINAL_PART_DATA_BODY')}
      </div>
      <PreferenceToggle
        id="ReplacementModal"
        isAvailabilityToggled={isAvailabilityToggled}
        setIsAvailabilityToggled={setIsAvailabilityToggled}
      />
      <div className="pb-3 pt-3 pt-lg-2">
        {Object.keys(currentItems)
          .filter(key => !!currentItems[key].replacementParts?.length)
          .map((key, idx) => (
            <ReplacementPartsTable
              key={currentItems[key].partNumber}
              index={(+idx + 1).toString()}
              item={currentItems[key]}
              isAvailabilityToggled={isAvailabilityToggled}
            />
          ))}
      </div>
      <Disclaimers />
      <StickySection
        className={cx(
          QOStyles['sticky-section'],
          'd-flex justify-content-between mt-3 ps-2 pe-4 py-3 align-items-center bg-white z-index-1 shadow rounded'
        )}
      >
        <AlloyButton buttonType="ghost" onClick={() => onBackCancel(!showBack)}>
          {showBack ? t('BACK') : t('CANCEL')}
        </AlloyButton>
        <div className="pe-2">
          <AlloyButton
            buttonType="primary"
            onClick={onUpdate}
            disabled={!!error}
          >
            {t('CONTINUE')}
          </AlloyButton>
        </div>
      </StickySection>
      <ChildModal
        title={alternateModalTitle}
        {...childModalOptions}
        backButtonMessage={backButtonMessage}
        close={handleChildModalClose}
      >
        <AlternativePartsModal
          modalOptions={childModalOptions}
          isChildModal
          isAvailabilityToggled={isAvailabilityToggled}
          setIsAvailabilityToggled={setIsAvailabilityToggled}
        />
      </ChildModal>
    </>
  );
};

ReplacementPartsModal.propTypes = {
  isAvailabilityToggled: PropTypes.bool,
  setIsAvailabilityToggled: PropTypes.func,
  isChildModal: PropTypes.bool,
  modalOptions: PropTypes.shape({
    isChild: PropTypes.bool,
    isOpen: PropTypes.bool,
    open: PropTypes.func,
    close: PropTypes.func
  })
};
export default ReplacementPartsModal;
